import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Package, Plus, Edit, Trash2, AlertTriangle, Truck, Star, X, Printer, QrCode, MapPin, History, Power, CheckCircle2, XCircle, RefreshCw, Repeat, ChevronRight, Copy } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';
import { Combobox } from '../../components/ui/combobox';
import { SupplierAutocomplete } from '../../components/ui/supplier-autocomplete';
import { AuditLogDialog } from '../../components/AuditLogDialog';
import { useIsMobile } from '../../hooks/useIsMobile';

// Inline search component for substitutes
const SubstituteSearch = ({ articles, currentId, excludeIds, onSelect }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = query.length >= 2
    ? articles.filter(a => 
        a.id !== currentId && 
        !excludeIds.includes(a.id) &&
        (a.name?.toLowerCase().includes(query.toLowerCase()) || a.code?.toLowerCase().includes(query.toLowerCase()))
      ).slice(0, 8)
    : [];

  return (
    <div className="relative">
      <Input
        placeholder="Cauta articol substitut (min 2 caractere)..."
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className="h-9 text-sm"
        data-testid="substitute-search-input"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(a => (
            <button key={a.id} type="button"
              onClick={() => { onSelect(a.id); setQuery(''); setOpen(false); }}
              className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center justify-between text-sm border-b last:border-0"
              data-testid={`substitute-option-${a.id}`}>
              <div className="min-w-0">
                <p className="font-medium truncate">{a.name}</p>
                <p className="text-xs text-slate-500 font-mono">{a.code}</p>
              </div>
              {a.total_stock > 0 && (
                <Badge variant="outline" className="text-xs shrink-0 ml-2">Stoc: {a.total_stock}</Badge>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ConsumablesPage = () => {
  const [articles, setArticles] = useState([]);
  const isMobile = useIsMobile(1024);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [bins, setBins] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [auditDialog, setAuditDialog] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [showBulkUpdateDialog, setShowBulkUpdateDialog] = useState(false);
  const [bulkUpdateFields, setBulkUpdateFields] = useState({});
  const [bulkUpdateValues, setBulkUpdateValues] = useState({});
  
  const [formData, setFormData] = useState({
    name: '', 
    code: '', 
    category: '', 
    unit: 'buc', 
    description: '', 
    min_stock: 0, 
    lot_required: false,
    suppliers: [],
    substitute_ids: [],
    default_bin_id: null,
    alternative_bin_ids: [],
    preferred_plant_id: null,
    storage_instructions: '',
    xrp_code: '',
    purchase_currency: '',
    xrp_subaccount: ''
  });

  useEffect(() => { 
    fetchBins();
    fetchPlants();
  }, []);

  const fetchBins = async () => {
    try {
      const response = await api.get('/bins');
      setBins(response.data);
    } catch (error) {
      console.error('Error fetching bins:', error);
    }
  };

  const fetchPlants = async () => {
    try {
      const response = await api.get('/plants');
      setPlants(response.data);
    } catch (error) {
      console.error('Error fetching plants:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const showInactive = statusFilter !== 'active';
      const [articlesRes, categoriesRes, suppliersRes] = await Promise.all([
        api.get(`/articles${showInactive ? '?show_inactive=true' : ''}`),
        api.get('/consumable-categories'),
        api.get('/suppliers')
      ]);
      let data = articlesRes.data;
      if (statusFilter === 'inactive') data = data.filter(a => !a.is_active);
      else if (statusFilter === 'active') data = data.filter(a => a.is_active !== false);
      setArticles(data);
      setCategories(categoriesRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        default_bin_id: formData.default_bin_id || null,
        preferred_plant_id: formData.preferred_plant_id || null,
        storage_instructions: formData.storage_instructions || null
      };
      if (editArticle) {
        await api.put(`/articles/${editArticle.id}`, submitData);
        // Update bidirectional substitutes
        await api.put(`/supplier-intelligence/articles/${editArticle.id}/substitutes`, { substitute_ids: formData.substitute_ids });
        toast.success('Articol actualizat!');
      } else {
        const res = await api.post('/articles', submitData);
        // If new article has substitutes, set them
        if (formData.substitute_ids.length > 0 && res.data?.id) {
          await api.put(`/supplier-intelligence/articles/${res.data.id}/substitutes`, { substitute_ids: formData.substitute_ids });
        }
        toast.success('Articol creat!');
      }
      setShowAddDialog(false);
      setEditArticle(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (article) => {
    setEditArticle(article);
    setFormData({
      name: article.name || '',
      code: article.code || '',
      category: article.category || '',
      unit: article.unit || 'buc',
      description: article.description || '',
      min_stock: article.min_stock || 0,
      lot_required: article.lot_required || false,
      suppliers: article.suppliers || [],
      substitute_ids: article.substitute_ids || [],
      default_bin_id: article.default_bin_id || null,
      alternative_bin_ids: article.alternative_bin_ids || [],
      preferred_plant_id: article.preferred_plant_id || null,
      storage_instructions: article.storage_instructions || '',
      xrp_code: article.xrp_code || '',
      purchase_currency: article.purchase_currency || '',
      xrp_subaccount: article.xrp_subaccount || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti?')) return;
    try {
      await api.delete(`/articles/${id}`);
      toast.success('Articol sters!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.patch(`/articles/${id}/toggle-active`);
      toast.success(currentStatus ? 'Articol dezactivat' : 'Articol activat');
      fetchData();
    } catch (error) {
      toast.error('Eroare la schimbarea statusului');
    }
  };

  const handleBulkStatus = async (isActive) => {
    if (!selectedIds.length) return;
    const action = isActive ? 'activa' : 'dezactiva';
    if (!window.confirm(`Sigur doriti sa ${action}ti ${selectedIds.length} articole?`)) return;
    try {
      await api.patch('/articles/bulk-status', { ids: selectedIds, is_active: isActive });
      toast.success(`${selectedIds.length} articole ${isActive ? 'activate' : 'dezactivate'}`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la operatia bulk');
    }
  };

  const handleBulkUpdateFields = async () => {
    const activeFields = Object.keys(bulkUpdateFields).filter(k => bulkUpdateFields[k]);
    if (!activeFields.length) { toast.error('Selectati cel putin un camp'); return; }
    const fields = {};
    activeFields.forEach(k => { fields[k] = bulkUpdateValues[k] ?? ''; });
    try {
      const res = await api.patch('/articles/bulk-update-fields', { ids: selectedIds, fields });
      toast.success(`${res.data.modified} articole actualizate (${res.data.fields_updated.length} campuri)`);
      setShowBulkUpdateDialog(false);
      setBulkUpdateFields({});
      setBulkUpdateValues({});
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la actualizarea in masa');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Sigur doriti sa stergeti DEFINITIV ${selectedIds.length} articole? Aceasta actiune nu poate fi anulata!`)) return;
    try {
      await api.patch('/articles/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} articole sterse`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergerea in masa');
    }
  };

  const handleDuplicate = async (article) => {
    try {
      const res = await api.post(`/articles/${article.id}/duplicate`);
      toast.success(`Articol duplicat: ${res.data.code} - ${res.data.name}`);
      fetchData();
      // Open the new article for editing
      const newArticle = res.data;
      setEditArticle(newArticle);
      setFormData({
        name: newArticle.name || '',
        code: newArticle.code || '',
        category: newArticle.category || '',
        unit: newArticle.unit || 'buc',
        description: newArticle.description || '',
        min_stock: newArticle.min_stock || 0,
        lot_required: newArticle.lot_required || false,
        suppliers: newArticle.suppliers || [],
        substitute_ids: newArticle.substitute_ids || [],
        default_bin_id: null,
        alternative_bin_ids: [],
        preferred_plant_id: newArticle.preferred_plant_id || null,
        storage_instructions: newArticle.storage_instructions || '',
        xrp_code: newArticle.xrp_code || '',
        purchase_currency: newArticle.purchase_currency || '',
        xrp_subaccount: newArticle.xrp_subaccount || ''
      });
      setShowAddDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la duplicare');
    }
  };

  const handleBulkDuplicate = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Doriti sa duplicati ${selectedIds.length} articole?`)) return;
    try {
      const res = await api.post('/articles/bulk-duplicate', { ids: selectedIds });
      toast.success(`${res.data.created} articole duplicate create`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la duplicarea in masa');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', code: '', category: '', unit: 'buc', description: '', min_stock: 0, lot_required: false, suppliers: [], substitute_ids: [],
      default_bin_id: null, alternative_bin_ids: [], preferred_plant_id: null, storage_instructions: '',
      xrp_code: '', purchase_currency: '', xrp_subaccount: ''
    });
  };

  const loadStockLocations = async (articleId) => {
    setStockLocationsLoading(true);
    try {
      const res = await api.get(`/articles/${articleId}/stock-locations`);
      setStockLocationsData(res.data);
      setShowStockLocationsDialog(true);
    } catch { toast.error('Eroare la incarcarea locatiilor'); }
    finally { setStockLocationsLoading(false); }
  };

  const [showQrExportDialog, setShowQrExportDialog] = useState(false);
  const [showStockLocationsDialog, setShowStockLocationsDialog] = useState(false);
  const [stockLocationsData, setStockLocationsData] = useState(null);
  const [stockLocationsLoading, setStockLocationsLoading] = useState(false);
  const [qrFields, setQrFields] = useState({
    code: true,
    name: true,
    unit: true,
    category: true,
    min_stock: false,
    description: false
  });

  const qrFieldOptions = [
    { key: 'code', label: 'Cod' },
    { key: 'name', label: 'Denumire' },
    { key: 'unit', label: 'Unitate masura' },
    { key: 'category', label: 'Categorie' },
    { key: 'min_stock', label: 'Stoc minim' },
    { key: 'description', label: 'Descriere' }
  ];

  const handleExportQrCodes = async (format = 'standard') => {
    try {
      const ids = selectedIds.length > 0 ? selectedIds : articles.map(a => a.id);
      const fields = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const response = await api.post(`/articles/qr-export?format=${format}&fields=${fields}`, ids, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `qr_articole_${ids.length}_${format}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowQrExportDialog(false);
      toast.success(`Export QR (${format}) pentru ${ids.length} articole generat!`);
    } catch (error) {
      toast.error('Eroare la export QR');
    }
  };

  const handleExportQrZip = async () => {
    try {
      const ids = selectedIds.length > 0 ? selectedIds : articles.map(a => a.id);
      const fields = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const response = await api.post(`/articles/qr-export-zip?fields=${fields}`, ids, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `qr_articole_${ids.length}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setShowQrExportDialog(false);
      toast.success(`Arhiva ZIP cu ${ids.length} imagini QR generata!`);
    } catch (error) {
      toast.error('Eroare la export ZIP');
    }
  };

  // Supplier management in form
  const addSupplier = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (!supplier) return;
    if (formData.suppliers.some(s => s.supplier_id === supplierId)) {
      toast.error('Furnizorul este deja adaugat');
      return;
    }
    
    setFormData({
      ...formData,
      suppliers: [...formData.suppliers, {
        supplier_id: supplierId,
        supplier_name: supplier.name,
        supplier_article_name: '',
        supplier_article_code: '',
        quality_grade: 'Standard',
        price: 0,
        lead_time_days: 1,
        is_preferred: formData.suppliers.length === 0 // First supplier is preferred by default
      }]
    });
  };

  const removeSupplier = (supplierId) => {
    const wasPreferred = formData.suppliers.find(s => s.supplier_id === supplierId)?.is_preferred;
    let newSuppliers = formData.suppliers.filter(s => s.supplier_id !== supplierId);
    
    // If removed was preferred, make first remaining preferred
    if (wasPreferred && newSuppliers.length > 0) {
      newSuppliers[0].is_preferred = true;
    }
    
    setFormData({ ...formData, suppliers: newSuppliers });
  };

  const updateSupplierField = (supplierId, field, value) => {
    setFormData({
      ...formData,
      suppliers: formData.suppliers.map(s => 
        s.supplier_id === supplierId ? { ...s, [field]: value } : s
      )
    });
  };

  const setPreferredSupplier = (supplierId) => {
    setFormData({
      ...formData,
      suppliers: formData.suppliers.map(s => ({
        ...s,
        is_preferred: s.supplier_id === supplierId
      }))
    });
  };

  // Prepare articles data with computed fields
  const articlesData = articles.map(a => ({
    ...a,
    category_name: categories.find(c => c.id === a.category)?.name || '-',
    preferred_supplier: a.suppliers?.find(s => s.is_preferred)?.supplier_name || '-',
    preferred_price: a.suppliers?.find(s => s.is_preferred)?.price || 0,
    suppliers_count: a.suppliers?.length || 0,
    substitutes_count: a.substitute_ids?.length || 0
  }));

  const tableColumns = [
    {
      key: 'code',
      label: 'Cod',
      sortable: true,
      filterable: true,
      render: (value) => <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{value}</span>
    },
    {
      key: 'name',
      label: 'Denumire',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4 text-slate-400" />
          <span className="font-medium">{value}</span>
          {row?.lot_required && <Badge variant="outline" className="text-xs">LOT</Badge>}
        </div>
      )
    },
    {
      key: 'category_name',
      label: 'Categorie',
      sortable: true,
      filterable: true
    },
    {
      key: 'unit',
      label: 'UM',
      sortable: true
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (value) => value === false
        ? <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">Inactiv</Badge>
        : <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Activ</Badge>
    },
    {
      key: 'preferred_supplier',
      label: 'Furnizor Principal',
      sortable: true,
      filterable: true,
      render: (value, row) => row?.suppliers_count > 0 ? (
        <div className="flex items-center gap-1">
          <Star className="w-3 h-3 text-yellow-500" />
          <span>{value}</span>
          {row.suppliers_count > 1 && (
            <Badge variant="outline" className="text-xs ml-1">+{row.suppliers_count - 1}</Badge>
          )}
        </div>
      ) : <span className="text-slate-400">-</span>
    },
    {
      key: 'preferred_price',
      label: 'Pret',
      sortable: true,
      render: (value, row) => row?.preferred_price > 0 ? `${value} LEI` : '-'
    },
    {
      key: 'min_stock',
      label: 'Stoc Min',
      sortable: true,
      render: (value, row) => (
        <span className={row?.total_stock < value ? 'text-red-600 font-medium' : ''}>
          {value}
        </span>
      )
    },
    {
      key: 'default_bin_code',
      label: 'BIN',
      sortable: false,
      render: (value, row) => row?.default_bin_id ? (
        <button onClick={(e) => { e.stopPropagation(); loadStockLocations(row.id); }} 
          className="flex items-center gap-1 text-xs hover:underline" title="Vezi toate locatiile stoc"
          data-testid={`view-stock-locations-${row.code}`}>
          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
          <span className="text-blue-600 font-mono">{value || 'Setat'}</span>
        </button>
      ) : (
        <span className="text-slate-400 text-xs">-</span>
      )
    },
    {
      key: 'actions',
      label: 'Actiuni',
      sortable: false,
      render: (_, row) => row ? (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleToggleActive(row.id, row.is_active !== false)}
            title={row.is_active === false ? 'Activeaza' : 'Dezactiveaza'}
            data-testid={`toggle-article-${row.code}`}
            className={row.is_active === false ? 'text-green-600' : 'text-orange-500'}
          >
            <Power className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDuplicate(row)} title="Duplica Articol" data-testid={`duplicate-article-${row.code}`} className="text-purple-600"><Copy className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setAuditDialog(row)} title="Istoric Modificari" data-testid={`audit-article-${row.code}`} className="text-amber-600"><History className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => loadStockLocations(row.id)} title="Locatii stoc BIN" data-testid={`stock-locations-${row.code}`} className="text-blue-600"><MapPin className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)} data-testid={`edit-article-${row.code}`}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600" data-testid={`delete-article-${row.code}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ) : null
    }
  ];

  return (
    <div className="space-y-6" data-testid="consumables-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Articole / Consumabile</h1>
          <p className="text-slate-600 mt-1">Gestioneaza articolele si furnizorii ({articles.length} total)</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-1" data-testid="status-filter-articles">
            {[{v:'active',l:'Active'},{v:'inactive',l:'Inactive'},{v:'all',l:'Toate'}].map(f => (
              <button key={f.v} onClick={() => setStatusFilter(f.v)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${statusFilter === f.v ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`filter-${f.v}`}
              >{f.l}</button>
            ))}
          </div>
          <Button variant="outline" onClick={() => setShowQrExportDialog(true)} className="h-10">
            <Printer className="w-4 h-4 mr-2" />
            Print QR {selectedIds.length > 0 && `(${selectedIds.length})`}
          </Button>
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditArticle(null); resetForm(); } }}>
            <DialogTrigger asChild>
              <Button className="btn-primary h-12" data-testid="add-consumable-btn">
                <Plus className="w-5 h-5 mr-2" />ARTICOL NOU
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editArticle ? 'Editeaza Articol' : 'Adauga Articol'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cod</Label>
                  <Input 
                    value={editArticle ? formData.code : '(auto-generat)'} 
                    disabled
                    className="h-11 bg-slate-100 text-slate-500 font-mono"
                    data-testid="article-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger className="h-11"><SelectValue placeholder="Selecteaza" /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Denumire *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                  required 
                  className="h-11"
                  placeholder="Denumire articol" 
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Unitate masura</Label>
                  <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buc">buc</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="l">litri</SelectItem>
                      <SelectItem value="m">metri</SelectItem>
                      <SelectItem value="set">set</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="g">grame</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Stoc minim</Label>
                  <Input 
                    type="number" 
                    value={formData.min_stock === 0 ? '' : formData.min_stock} 
                    onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) || 0 })} 
                    className="h-11" 
                    min="0"
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2 flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={formData.lot_required} onCheckedChange={(v) => setFormData({ ...formData, lot_required: v })} />
                    <Label className="text-sm">Necesita LOT</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                  rows={2}
                  placeholder="Descriere articol..." 
                />
              </div>

              <Separator />

              {/* XRP Fields */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Cod Articol XRP</Label>
                  <Input 
                    value={formData.xrp_code} 
                    onChange={(e) => setFormData({ ...formData, xrp_code: e.target.value })} 
                    className="h-11"
                    placeholder="Cod XRP" 
                    data-testid="article-xrp-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Valuta achizitie</Label>
                  <Select value={formData.purchase_currency} onValueChange={(v) => setFormData({ ...formData, purchase_currency: v })}>
                    <SelectTrigger className="h-11" data-testid="article-currency">
                      <SelectValue placeholder="Selecteaza" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RON">RON</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Subcont XRP</Label>
                  <Input 
                    value={formData.xrp_subaccount} 
                    onChange={(e) => setFormData({ ...formData, xrp_subaccount: e.target.value })} 
                    className="h-11"
                    placeholder="Subcont" 
                    data-testid="article-xrp-subaccount"
                  />
                </div>
              </div>

              <Separator />

              {/* Suppliers Section - Enhanced Card Layout */}
              <div className="space-y-3" data-testid="suppliers-section">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-base font-semibold">
                    <Truck className="w-5 h-5 text-blue-600" />
                    Furnizori ({formData.suppliers.length})
                  </Label>
                  <SupplierAutocomplete
                    suppliers={suppliers}
                    excludeIds={formData.suppliers.map(s => s.supplier_id)}
                    onSelect={(supplier) => {
                      if (supplier) addSupplier(supplier.id);
                    }}
                    placeholder="Adauga furnizor..."
                    className="w-56"
                    triggerClassName="h-9"
                  />
                </div>

                {formData.suppliers.length === 0 ? (
                  <div className="text-center py-8 border-2 rounded-lg border-dashed border-slate-200 bg-slate-50/50">
                    <Truck className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500 font-medium">Niciun furnizor configurat</p>
                    <p className="text-slate-400 text-sm mt-1">Adaugati furnizori pentru a vedea comparatii de preturi si stoc</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.suppliers.map((supplier, idx) => {
                      const cheapest = formData.suppliers.length > 1 && 
                        formData.suppliers.filter(s => s.price > 0).length > 0 &&
                        supplier.price > 0 &&
                        supplier.price === Math.min(...formData.suppliers.filter(s => s.price > 0).map(s => s.price));
                      const mostExpensive = formData.suppliers.length > 1 && 
                        formData.suppliers.filter(s => s.price > 0).length > 0 &&
                        supplier.price > 0 &&
                        supplier.price === Math.max(...formData.suppliers.filter(s => s.price > 0).map(s => s.price)) && !cheapest;
                      
                      return (
                        <div key={supplier.supplier_id} 
                          className={`border-2 rounded-lg p-4 transition-all ${supplier.is_preferred ? 'border-blue-300 bg-blue-50/50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                          data-testid={`supplier-card-${idx}`}
                        >
                          {/* Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-slate-900">{supplier.supplier_name}</span>
                              {supplier.is_preferred && (
                                <Badge className="bg-blue-600 text-white text-xs">PREFERAT</Badge>
                              )}
                              {cheapest && (
                                <Badge className="bg-emerald-600 text-white text-xs">CEL MAI IEFTIN</Badge>
                              )}
                              {mostExpensive && (
                                <Badge variant="outline" className="border-orange-300 text-orange-600 text-xs">CEL MAI SCUMP</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {!supplier.is_preferred && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => setPreferredSupplier(supplier.supplier_id)}
                                  title="Seteaza ca preferat" className="text-slate-400 hover:text-blue-600" data-testid={`set-preferred-${idx}`}>
                                  <Star className="w-4 h-4" />
                                </Button>
                              )}
                              {supplier.is_preferred && (
                                <Star className="w-4 h-4 text-blue-600 fill-blue-600 mx-1" />
                              )}
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeSupplier(supplier.supplier_id)}
                                className="text-red-400 hover:text-red-600" data-testid={`remove-supplier-${idx}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {/* Fields Grid */}
                          <div className="space-y-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-slate-500">Denumire la furnizor</Label>
                              <Input
                                placeholder="Cum numeste furnizorul acest articol"
                                value={supplier.supplier_article_name || ''}
                                onChange={(e) => updateSupplierField(supplier.supplier_id, 'supplier_article_name', e.target.value)}
                                className="h-11"
                                data-testid={`supplier-article-name-${idx}`}
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Cod articol furnizor</Label>
                                <Input
                                  placeholder="Cod la furnizor (optional)"
                                  value={supplier.supplier_article_code || ''}
                                  onChange={(e) => updateSupplierField(supplier.supplier_id, 'supplier_article_code', e.target.value)}
                                  className="h-10"
                                  data-testid={`supplier-article-code-${idx}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Pret (RON)</Label>
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={supplier.price}
                                  onChange={(e) => updateSupplierField(supplier.supplier_id, 'price', parseFloat(e.target.value) || 0)}
                                  className="h-10 font-mono"
                                  data-testid={`supplier-price-${idx}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-slate-500">Calitate</Label>
                                <select
                                  value={supplier.quality_grade || 'Standard'}
                                  onChange={(e) => updateSupplierField(supplier.supplier_id, 'quality_grade', e.target.value)}
                                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                  data-testid={`supplier-quality-${idx}`}
                                >
                                <option value="Premium">Premium</option>
                                <option value="Standard">Standard</option>
                                <option value="Economy">Economy</option>
                              </select>
                            </div>
                          </div>
                          </div>

                          {/* Price comparison bar */}
                          {formData.suppliers.length > 1 && supplier.price > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 w-16">Pret rel.</span>
                                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full ${cheapest ? 'bg-emerald-500' : mostExpensive ? 'bg-orange-400' : 'bg-blue-400'}`}
                                    style={{ width: `${Math.min(100, (supplier.price / Math.max(...formData.suppliers.filter(s => s.price > 0).map(s => s.price))) * 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs font-mono font-bold text-slate-700">{supplier.price.toFixed(2)}</span>
                              </div>
                            </div>
                          )}

                          {/* Auto-populated info from receptions */}
                          {supplier.last_reception_date && (
                            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500">
                              <span>Ultima receptie: {new Date(supplier.last_reception_date).toLocaleDateString('ro-RO')}</span>
                              {supplier.last_reception_price > 0 && (
                                <span>Pret receptie: <strong className="text-slate-700">{supplier.last_reception_price.toFixed(2)} RON</strong></span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Pretul se actualizeaza automat la fiecare receptie. Cautarea la receptie functioneaza si dupa denumirea furnizorului.
                </p>
              </div>

              <Separator />

              {/* Substitutes Section */}
              <div className="space-y-3" data-testid="substitutes-section">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-base font-semibold">
                    <Repeat className="w-5 h-5 text-purple-600" />
                    Articole Substitut ({formData.substitute_ids.length})
                  </Label>
                </div>
                
                {/* Add substitute search */}
                <SubstituteSearch
                  articles={articles}
                  currentId={editArticle?.id}
                  excludeIds={formData.substitute_ids}
                  onSelect={(artId) => {
                    if (!formData.substitute_ids.includes(artId)) {
                      setFormData({ ...formData, substitute_ids: [...formData.substitute_ids, artId] });
                    }
                  }}
                />

                {formData.substitute_ids.length === 0 ? (
                  <div className="text-center py-4 border rounded-lg border-dashed border-slate-200 bg-slate-50/50">
                    <Repeat className="w-8 h-8 mx-auto text-slate-300 mb-1" />
                    <p className="text-slate-500 text-sm">Niciun articol substitut configurat</p>
                    <p className="text-slate-400 text-xs mt-0.5">Cautati si adaugati articole echivalente care pot fi folosite ca inlocuitor</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.substitute_ids.map((subId) => {
                      const subArticle = articles.find(a => a.id === subId);
                      return (
                        <div key={subId} className="flex items-center justify-between p-3 border rounded-lg bg-purple-50/30 border-purple-200"
                          data-testid={`substitute-item-${subId}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <Package className="w-4 h-4 text-purple-500 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{subArticle?.name || subId}</p>
                              {subArticle?.code && <p className="text-xs text-slate-500 font-mono">{subArticle.code}</p>}
                            </div>
                            {subArticle?.total_stock > 0 && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs shrink-0">Stoc: {subArticle.total_stock}</Badge>
                            )}
                          </div>
                          <Button type="button" variant="ghost" size="sm"
                            onClick={() => setFormData({ ...formData, substitute_ids: formData.substitute_ids.filter(id => id !== subId) })}
                            className="text-red-400 hover:text-red-600 shrink-0"
                            data-testid={`remove-substitute-${subId}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Legatura este bidirectionala - articolul substitut va avea, la randul lui, articolul curent ca inlocuitor.
                </p>
              </div>

              <Separator />

              {/* Storage Settings Section */}
              <div className="space-y-4 bg-blue-50 p-4 rounded-lg" data-testid="storage-settings-section">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <Label className="text-base font-semibold text-blue-900">Locatie Implicita de Stocare</Label>
                </div>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>BIN Preferat Principal</Label>
                    <Combobox
                      items={bins.map(b => ({ 
                        id: b.id, 
                        name: `${b.cabinet} / ${b.shelf} / ${b.position}`,
                        code: b.code,
                        description: `Raft ${b.shelf} - Pozitie ${b.position}`
                      }))}
                      value={formData.default_bin_id || ''}
                      onSelect={(v) => setFormData({...formData, default_bin_id: v || null})}
                      placeholder="Cauta sau selecteaza BIN preferat..."
                      searchPlaceholder="Cauta cabinet, raft, pozitie sau cod..."
                      emptyText="Niciun BIN gasit"
                      nameKey="name"
                      codeKey="code"
                      className="bg-white"
                      clearable={true}
                    />
                    <p className="text-xs text-blue-700">
                      BIN-ul preferat va fi auto-completat in receptii pentru acest articol
                    </p>
                  </div>

                  {formData.default_bin_id && (
                    <div className="bg-green-50 p-3 rounded border border-green-200" data-testid="selected-bin-info">
                      <p className="text-sm text-green-900 font-semibold">
                        Locatie Preferata: {bins.find(b => b.id === formData.default_bin_id)?.code || formData.default_bin_id}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Instructiuni Stocare (optional)</Label>
                    <Textarea
                      value={formData.storage_instructions}
                      onChange={(e) => setFormData({...formData, storage_instructions: e.target.value})}
                      placeholder="Ex: Pastrati la loc uscat, departe de surse de caldura..."
                      rows={2}
                      className="bg-white"
                      data-testid="storage-instructions-input"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Anuleaza</Button>
                {editArticle && (
                  <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); handleDuplicate(editArticle); }} className="text-purple-700 border-purple-300 hover:bg-purple-50" data-testid="duplicate-from-edit-btn">
                    <Copy className="w-4 h-4 mr-2" />Duplica
                  </Button>
                )}
                <Button type="submit" className="btn-primary" data-testid="submit-article-btn">{editArticle ? 'ACTUALIZEAZA' : 'ADAUGA'}</Button>
              </DialogFooter>
            </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <>
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3" data-testid="bulk-actions-bar">
              <span className="text-sm font-medium text-blue-800">{selectedIds.length} selectate</span>
              <div className="flex gap-2 ml-auto flex-wrap">
                <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" onClick={() => { setBulkUpdateFields({}); setBulkUpdateValues({}); setShowBulkUpdateDialog(true); }} data-testid="bulk-update-fields-articles">
                  <RefreshCw className="w-4 h-4 mr-1" /> Actualizare in masa
                </Button>
                <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => handleBulkStatus(true)} data-testid="bulk-activate-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Activeaza
                </Button>
                <Button size="sm" variant="outline" className="text-purple-700 border-purple-300 hover:bg-purple-50" onClick={handleBulkDuplicate} data-testid="bulk-duplicate-btn">
                  <Copy className="w-4 h-4 mr-1" /> Duplica
                </Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => handleBulkStatus(false)} data-testid="bulk-deactivate-btn">
                  <XCircle className="w-4 h-4 mr-1" /> Dezactiveaza
                </Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={handleBulkDelete} data-testid="bulk-delete-articles">
                  <Trash2 className="w-4 h-4 mr-1" /> Sterge
                </Button>
              </div>
            </div>
          )}
          {isMobile ? (
          /* Mobile Card View */
          <div className="space-y-2" data-testid="articles-mobile-list">
            {articlesData.length === 0 ? (
              <div className="text-center py-12 text-slate-500">Nu exista articole inregistrate</div>
            ) : articlesData.map((row) => (
              <div
                key={row.id}
                className="bg-white rounded-xl border border-slate-200 p-3.5 active:bg-slate-50 transition-colors"
                onClick={() => handleEdit(row)}
                data-testid={`mobile-article-${row.code}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900 line-clamp-1">{row.name}</h3>
                    <p className="text-xs text-slate-500 font-mono">{row.code}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-2">
                  {row.category_name !== '-' && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{row.category_name}</Badge>}
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">{row.unit}</Badge>
                  {row.is_active === false
                    ? <Badge className="bg-red-100 text-red-600 text-[10px] px-1.5 py-0">Inactiv</Badge>
                    : <Badge className="bg-green-100 text-green-600 text-[10px] px-1.5 py-0">Activ</Badge>}
                  {row.lot_required && <Badge variant="outline" className="text-[10px] px-1.5 py-0">LOT</Badge>}
                </div>
                <div className="flex items-center gap-3 mt-2.5 pt-2 border-t border-slate-100 text-[11px] text-slate-500">
                  {row.preferred_supplier !== '-' && (
                    <span className="flex items-center gap-1 truncate">
                      <Star className="w-3 h-3 text-yellow-500 shrink-0" />{row.preferred_supplier}
                      {row.suppliers_count > 1 && <span className="text-slate-400">+{row.suppliers_count - 1}</span>}
                    </span>
                  )}
                  {row.preferred_price > 0 && <span className="font-mono font-medium text-slate-700">{row.preferred_price} LEI</span>}
                  <span className={`ml-auto font-medium ${row.total_stock < row.min_stock ? 'text-red-600' : 'text-slate-700'}`}>
                    Stoc min: {row.min_stock}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DataTable
          data={articlesData}
          columns={tableColumns}
          pageSize={10}
          searchable={true}
          exportable={true}
          printable={true}
          selectable={true}
          onSelectionChange={(ids) => setSelectedIds(ids)}
          onRowClick={handleEdit}
          emptyMessage="Nu exista articole inregistrate"
        />
        )}
        </>
      )}

      {/* QR Export Format Dialog */}
      <Dialog open={showQrExportDialog} onOpenChange={setShowQrExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Coduri QR</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Se vor exporta {selectedIds.length > 0 ? selectedIds.length : articles.length} articole
            </p>
            
            {/* Field Selection */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Campuri afisate pe eticheta:</p>
              <div className="grid grid-cols-2 gap-2">
                {qrFieldOptions.map(field => (
                  <div key={field.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`art-field-${field.key}`}
                      checked={qrFields[field.key]}
                      onCheckedChange={(checked) => setQrFields({ ...qrFields, [field.key]: checked })}
                    />
                    <label htmlFor={`art-field-${field.key}`} className="text-sm cursor-pointer">{field.label}</label>
                  </div>
                ))}
              </div>
            </div>
            
            <Separator />
            
            <div className="grid gap-3">
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleExportQrCodes('standard')}>
                <div className="text-left">
                  <p className="font-semibold">Standard (24/pagina)</p>
                  <p className="text-xs text-slate-500">3 coloane x 8 randuri - ideal pentru A4</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleExportQrCodes('large')}>
                <div className="text-left">
                  <p className="font-semibold">Mari (8/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 4 randuri - etichete mari vizibile</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" data-testid="qr-format-industrial" onClick={() => handleExportQrCodes('industrial')}>
                <div className="text-left">
                  <p className="font-semibold">Industrial (16/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 8 randuri - 105x38mm etichete mici</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleExportQrCodes('thermal')}>
                <div className="text-left">
                  <p className="font-semibold">Termic (50x30mm)</p>
                  <p className="text-xs text-slate-500">Pentru imprimante de etichete termice</p>
                </div>
              </Button>
              <Separator className="my-2" />
              <Button variant="outline" className="h-auto py-4 justify-start border-blue-200 hover:bg-blue-50" onClick={handleExportQrZip}>
                <div className="text-left">
                  <p className="font-semibold text-blue-700">Export JPEG (ZIP)</p>
                  <p className="text-xs text-slate-500">Imagini individuale cu campurile selectate</p>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Fields Dialog */}
      <Dialog open={showBulkUpdateDialog} onOpenChange={setShowBulkUpdateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Actualizare in masa</DialogTitle>
            <DialogDescription>Selectati campurile pe care doriti sa le actualizati pentru {selectedIds.length} articole</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {[
              { key: 'category', label: 'Categorie', type: 'select' },
              { key: 'unit', label: 'Unitate masura', type: 'unit' },
              { key: 'min_stock', label: 'Stoc minim', type: 'number' },
              { key: 'default_bin_id', label: 'BIN Preferat Principal', type: 'bin' },
              { key: 'preferred_plant_id', label: 'Sediu preferat', type: 'plant' },
              { key: 'lot_required', label: 'Lot obligatoriu', type: 'boolean' },
              { key: 'is_active', label: 'Status articol', type: 'status' },
              { key: 'xrp_code', label: 'Cod XRP', type: 'text' },
              { key: 'purchase_currency', label: 'Moneda achizitie', type: 'currency' },
              { key: 'xrp_subaccount', label: 'Subcont XRP', type: 'text' },
              { key: 'storage_instructions', label: 'Instructiuni depozitare', type: 'text' },
              { key: 'description', label: 'Descriere', type: 'text' },
            ].map(field => (
              <div key={field.key} className={`rounded-lg border p-3 transition-colors ${bulkUpdateFields[field.key] ? 'border-blue-300 bg-blue-50/50' : 'border-slate-200'}`}>
                <div className="flex items-center gap-2">
                  <Checkbox id={`bulk-art-${field.key}`} checked={!!bulkUpdateFields[field.key]}
                    onCheckedChange={(checked) => {
                      setBulkUpdateFields(prev => ({ ...prev, [field.key]: checked }));
                      if (!checked) setBulkUpdateValues(prev => { const n = { ...prev }; delete n[field.key]; return n; });
                    }} data-testid={`bulk-art-check-${field.key}`} />
                  <label htmlFor={`bulk-art-${field.key}`} className="text-sm font-medium cursor-pointer flex-1">{field.label}</label>
                </div>
                {bulkUpdateFields[field.key] && (
                  <div className="mt-2 pl-6">
                    {field.key === 'category' ? (
                      <Select value={bulkUpdateValues.category || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, category: v }))}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Selecteaza categorie" /></SelectTrigger>
                        <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.type === 'unit' ? (
                      <Select value={bulkUpdateValues.unit || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, unit: v }))}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Selecteaza unitate" /></SelectTrigger>
                        <SelectContent>
                          {['buc', 'kg', 'l', 'm', 'set', 'pereche', 'cutie', 'rola', 'tub'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : field.type === 'bin' ? (
                      <Combobox
                        items={bins.map(b => ({ 
                          id: b.id, 
                          name: `${b.cabinet} / ${b.shelf} / ${b.position}`,
                          code: b.code
                        }))}
                        value={bulkUpdateValues.default_bin_id || ''}
                        onSelect={(v) => setBulkUpdateValues(prev => ({ ...prev, default_bin_id: v || null }))}
                        placeholder="Cauta sau selecteaza BIN..."
                        searchPlaceholder="Cauta cabinet, raft, pozitie..."
                        emptyText="Niciun BIN gasit"
                        nameKey="name"
                        codeKey="code"
                        clearable={true}
                      />
                    ) : field.type === 'plant' ? (
                      <Select value={bulkUpdateValues.preferred_plant_id || '__clear__'} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, preferred_plant_id: v === '__clear__' ? null : v }))}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Selecteaza sediu" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__clear__">-- Fara sediu preferat --</SelectItem>
                          {plants.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : field.type === 'boolean' ? (
                      <div className="flex items-center gap-3">
                        <Switch 
                          checked={bulkUpdateValues.lot_required || false} 
                          onCheckedChange={(v) => setBulkUpdateValues(prev => ({ ...prev, lot_required: v }))} 
                        />
                        <span className="text-sm text-slate-600">{bulkUpdateValues.lot_required ? 'Da, lot obligatoriu' : 'Nu, lot optional'}</span>
                      </div>
                    ) : field.type === 'status' ? (
                      <Select value={bulkUpdateValues.is_active === false ? 'inactive' : 'active'} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, is_active: v === 'active' }))}>
                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active"><div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Activ</div></SelectItem>
                          <SelectItem value="inactive"><div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /> Inactiv</div></SelectItem>
                        </SelectContent>
                      </Select>
                    ) : field.type === 'currency' ? (
                      <Select value={bulkUpdateValues.purchase_currency || ''} onValueChange={v => setBulkUpdateValues(prev => ({ ...prev, purchase_currency: v }))}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Selecteaza moneda" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="RON">RON</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : field.type === 'number' ? (
                      <Input type="number" value={bulkUpdateValues[field.key] || ''} onChange={e => setBulkUpdateValues(prev => ({ ...prev, [field.key]: parseInt(e.target.value) || 0 }))} className="h-10" placeholder={`Noua valoare`} data-testid={`bulk-art-val-${field.key}`} />
                    ) : (
                      <Input value={bulkUpdateValues[field.key] || ''} onChange={e => setBulkUpdateValues(prev => ({ ...prev, [field.key]: e.target.value }))} className="h-10" placeholder={`Noua valoare ${field.label.toLowerCase()}`} data-testid={`bulk-art-val-${field.key}`} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkUpdateDialog(false)}>Anuleaza</Button>
            <Button className="btn-primary" onClick={handleBulkUpdateFields} disabled={!Object.values(bulkUpdateFields).some(v => v)} data-testid="bulk-update-submit-articles">
              <RefreshCw className="w-4 h-4 mr-2" />Actualizeaza {selectedIds.length} articole
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuditLogDialog
        open={!!auditDialog}
        onOpenChange={(open) => !open && setAuditDialog(null)}
        entityType="article"
        entityId={auditDialog?.id}
        entityName={auditDialog ? `${auditDialog.code} - ${auditDialog.name}` : ''}
      />

      {/* Stock Locations Dialog */}
      <Dialog open={showStockLocationsDialog} onOpenChange={setShowStockLocationsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Locatii Stoc
            </DialogTitle>
            {stockLocationsData && (
              <p className="text-sm text-slate-600">
                {stockLocationsData.article_name} ({stockLocationsData.article_code}) &middot; Total: <span className="font-bold text-slate-900">{stockLocationsData.total_stock}</span>
              </p>
            )}
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {stockLocationsLoading ? (
              <p className="text-center text-slate-500 py-8">Se incarca...</p>
            ) : stockLocationsData?.locations?.length === 0 ? (
              <p className="text-center text-slate-500 py-8">Niciun stoc in BIN-uri</p>
            ) : stockLocationsData?.locations?.map((loc, i) => (
              <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${loc.is_preferred ? 'bg-yellow-50 border-yellow-200' : 'bg-slate-50 border-slate-200'}`}
                data-testid={`stock-location-${loc.bin_code}`}>
                <div className="flex items-center gap-3">
                  {loc.is_preferred ? (
                    <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center" title="BIN Preferat">
                      <Star className="w-4 h-4 text-yellow-600 fill-yellow-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                      <MapPin className="w-4 h-4 text-slate-500" />
                    </div>
                  )}
                  <div>
                    <p className="font-mono font-medium text-sm">{loc.cabinet} → {loc.shelf} → {loc.position}</p>
                    <p className="text-xs text-slate-500">{loc.bin_code} {loc.plant_name ? `· ${loc.plant_name}` : ''}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold font-mono text-emerald-600">{loc.quantity}</p>
                  {loc.is_preferred && <p className="text-[10px] text-yellow-600 font-medium">PREFERAT</p>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsumablesPage;
